import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  ChevronRight, ChevronDown, Plus, Folder, Code2, Globe,
  Database, FlaskConical, LogOut, FolderOpen, Trash2, Search,
  Server, Play, BookOpen, BarChart3, Bell, Ticket, ShieldCheck, Rocket
} from 'lucide-react';
import BrandLogo from './BrandLogo';
import ThemeToggle from './ThemeToggle';

const METHOD_COLORS = { GET:'#34d399', POST:'#4f9cf9', PUT:'#fb923c', PATCH:'#a78bfa', DELETE:'#f87171', HEAD:'#4d6080', OPTIONS:'#4d6080' };

const NAV_ADMIN = [
  { id:'overview',     label:'Overview',          icon: BarChart3   },
  { id:'endpoints',    label:'APIs',              icon: Code2       },
  { id:'environments', label:'Environments',      icon: Globe       },
  { id:'schemas',      label:'Schemas',           icon: Database    },
  { id:'mock',         label:'Mock Server',       icon: Server      },
  { id:'runner',       label:'Collection Runner', icon: Play        },
  { id:'docs',         label:'Documentation',     icon: BookOpen    },
  { id:'publish',      label:'Publish Docs',      icon: Rocket      },
  { id:'tests',        label:'Test Runs',         icon: FlaskConical},
  { id:'tickets',      label:'Support Tickets',   icon: Ticket      },
];
const NAV_CLIENT = [
  { id:'docs',         label:'API Docs',          icon: BookOpen    },
  { id:'endpoints',    label:'Test APIs',         icon: Code2       },
  { id:'tickets',      label:'My Tickets',        icon: Ticket      },
];
const NAV_AGENT = [
  { id:'overview',     label:'Overview',          icon: BarChart3   },
  { id:'endpoints',    label:'APIs',              icon: Code2       },
  { id:'docs',         label:'Docs',              icon: BookOpen    },
  { id:'tickets',      label:'Support Queue',     icon: Ticket      },
];

export default function Sidebar({ projectId, onEndpointSelect, selectedEndpointId, onRefresh, activeView }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWs, setActiveWs] = useState(null);
  const [projects, setProjects] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [folders, setFolders] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [notifCount, setNotifCount] = useState(0);
  const [showWsPicker, setShowWsPicker] = useState(false);

  const isClient = user?.role === 'client';
  const isAgent  = user?.role === 'agent';
  const isAdmin  = ['admin','member'].includes(user?.role);

  const navItems = isClient ? NAV_CLIENT : isAgent ? NAV_AGENT : NAV_ADMIN;

  useEffect(() => { init(); pollNotifs(); }, []);
  useEffect(() => { if (activeWs) loadProjects(activeWs.id); }, [activeWs]);
  useEffect(() => { if (projectId) loadEndpoints(projectId); }, [projectId, onRefresh]);

  async function init() {
    try {
      if (isClient) {
        const res = await api.get('/my-assigned-projects');
        setProjects(res.data);
      } else {
        const res = await api.get('/workspaces');
        setWorkspaces(res.data);
        if (res.data.length) setActiveWs(res.data[0]);
      }
    } catch {}
  }

  async function loadProjects(wsId) {
    try { const r = await api.get(`/workspaces/${wsId}/projects`); setProjects(r.data); } catch {}
  }
  async function loadEndpoints(pid) {
    try { const r = await api.get(`/projects/${pid}/endpoints`); setEndpoints(r.data.endpoints||[]); setFolders(r.data.folders||[]); } catch {}
  }
  async function pollNotifs() {
    try { const r = await api.get('/notifications'); setNotifCount(r.data.unread||0); } catch {}
  }

  const toggle = id => setExpanded(e => ({ ...e, [id]: !e[id] }));
  const filtered = endpoints.filter(e => !search || e.name.toLowerCase().includes(search.toLowerCase()) || e.path.toLowerCase().includes(search.toLowerCase()));

  async function createFolder() {
    const n = prompt('Folder name:'); if (!n) return;
    try { await api.post(`/projects/${projectId}/folders`, { name: n }); loadEndpoints(projectId); } catch {}
  }

  async function delEp(id, e) {
    e.stopPropagation(); if (!window.confirm('Delete?')) return;
    try { await api.delete(`/endpoints/${id}`); loadEndpoints(projectId); toast.success('Deleted'); } catch {}
  }

  return (
    <aside style={S.sidebar}>
      {/* Logo */}
      <div style={S.head}>
        <div style={S.logoRow}>
          <BrandLogo height={30} />
          <div style={S.logoMeta}>
            <span className={`role-chip role-${user?.role}`}>{user?.role}</span>
          </div>
          {notifCount > 0 && (
            <button style={S.bell} onClick={() => navigate('/notifications')}>
              <Bell size={14} />
              <span style={S.bellBadge}>{notifCount > 9 ? '9+' : notifCount}</span>
            </button>
          )}
        </div>

        {/* Workspace picker */}
        {!isClient && activeWs && (
          <div style={S.wsSel} onClick={() => setShowWsPicker(v => !v)}>
            <div style={S.wsIcon}>{activeWs.name[0]}</div>
            <span style={S.wsLabel}>{activeWs.name}</span>
            <ChevronDown size={11} style={{ color:'var(--text-3)', transition:'transform .2s', transform: showWsPicker ? 'rotate(180deg)':'none' }} />
          </div>
        )}
        {isClient && <div style={{ fontSize:11, color:'var(--text-3)', marginTop:6 }}>Assigned Projects</div>}

        {showWsPicker && (
          <div style={S.picker}>
            {workspaces.map(ws => (
              <div key={ws.id} style={{ ...S.pickerItem, ...(activeWs?.id===ws.id ? S.pickerActive : {}) }}
                onClick={() => { setActiveWs(ws); setShowWsPicker(false); navigate('/'); }}>
                <div style={{ ...S.wsIcon, width:18, height:18, fontSize:9 }}>{ws.name[0]}</div>
                <span>{ws.name}</span>
              </div>
            ))}
            <div style={{ height:1, background:'var(--border)', margin:'4px 0' }} />
            <div style={{ ...S.pickerItem, color:'var(--accent)' }} onClick={() => { navigate('/'); setShowWsPicker(false); }}>
              <Plus size={11} /> New Workspace
            </div>
          </div>
        )}
      </div>

      {/* Back btn */}
      {projectId && (
        <button style={S.backBtn} onClick={() => navigate('/')}>
          <ChevronRight size={11} style={{ transform:'rotate(180deg)' }} /> All Projects
        </button>
      )}

      {/* Content */}
      {!projectId ? (
        /* Project list */
        <div style={S.projList}>
          <div style={S.secHead}>
            <span style={S.secLabel}>Projects</span>
            {isAdmin && <button className="btn-icon" style={{ padding:3 }} onClick={() => navigate('/')}><Plus size={12}/></button>}
          </div>
          {projects.map(p => (
            <div key={p.id} style={S.projItem} onClick={() => navigate(`/project/${p.id}`)}>
              <div style={S.projDot} />
              <span style={S.projName}>{p.name}</span>
              {p.assignment_role && <span style={S.asgBadge}>{p.assignment_role}</span>}
            </div>
          ))}
          {!projects.length && <div style={S.noItems}>No projects yet</div>}
        </div>
      ) : (
        /* Project nav */
        <div style={S.navWrap}>
          <div style={S.navList}>
            {navItems.map(n => (
              <button key={n.id} style={{ ...S.navItem, ...(activeView===n.id ? S.navActive : {}) }}
                onClick={() => onEndpointSelect && onEndpointSelect(null, n.id)}>
                <n.icon size={14} style={{ flexShrink:0 }} />
                <span>{n.label}</span>
                {n.id==='tickets' && notifCount>0 && <span style={S.navBadge}>{notifCount}</span>}
              </button>
            ))}
          </div>

          {/* Endpoint tree */}
          {activeView === 'endpoints' && (
            <div style={S.tree}>
              <div style={S.treeTop}>
                <div style={S.srchWrap}>
                  <Search size={10} style={{ color:'var(--text-3)' }} />
                  <input style={S.srchInput} placeholder="Search APIs..." value={search} onChange={e=>setSearch(e.target.value)} />
                </div>
                {isAdmin && <button className="btn-icon" style={{ padding:3 }} onClick={createFolder}><FolderOpen size={12}/></button>}
              </div>
              <div style={S.treeBody}>
                {folders.map(f => {
                  const feps = filtered.filter(e => e.folder_id === f.id);
                  const open = expanded[f.id];
                  return (
                    <div key={f.id}>
                      <div style={S.folderRow} onClick={() => toggle(f.id)}>
                        {open ? <ChevronDown size={10}/> : <ChevronRight size={10}/>}
                        <Folder size={11} style={{ color:'#fb923c' }} />
                        <span style={S.folderName}>{f.name}</span>
                        <span style={S.cnt}>{feps.length}</span>
                        {isAdmin && <button style={S.del} onClick={e => { e.stopPropagation(); if(window.confirm('Delete folder?')) api.delete(`/folders/${f.id}`).then(()=>loadEndpoints(projectId)); }}><Trash2 size={9}/></button>}
                      </div>
                      {open && feps.map(ep => <EpRow key={ep.id} ep={ep} sel={selectedEndpointId===ep.id} onClick={() => onEndpointSelect(ep)} onDel={isAdmin ? e=>delEp(ep.id,e) : null} indent />)}
                    </div>
                  );
                })}
                {filtered.filter(e=>!e.folder_id).map(ep => <EpRow key={ep.id} ep={ep} sel={selectedEndpointId===ep.id} onClick={() => onEndpointSelect(ep)} onDel={isAdmin ? e=>delEp(ep.id,e) : null} />)}
                {!filtered.length && <div style={S.noItems}>No APIs found</div>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={S.footer}>
        {isAdmin && (
          <button style={S.adminBtn} onClick={() => navigate('/admin')}>
            <ShieldCheck size={13} /> Admin Panel
          </button>
        )}
        <div style={S.userRow}>
          <div className="avatar avatar-sm">{user?.name?.[0]?.toUpperCase()}</div>
          <div style={{ flex:1, overflow:'hidden' }}>
            <div style={{ fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.name}</div>
            <div style={{ fontSize:10, color:'var(--text-3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.email}</div>
          </div>
          <ThemeToggle />
          <button className="btn-icon" onClick={logout} title="Sign out"><LogOut size={13}/></button>
        </div>
      </div>
    </aside>
  );
}

function EpRow({ ep, sel, onClick, onDel, indent }) {
  const [hov, setHov] = useState(false);
  return (
    <div style={{ ...S.epRow, ...(sel?S.epSel:hov?S.epHov:{}), paddingLeft:indent?28:10 }}
      onClick={onClick} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}>
      <span style={{ fontFamily:'var(--mono)', fontWeight:700, fontSize:9, minWidth:40, color:METHOD_COLORS[ep.method]||'#4d6080' }}>{ep.method}</span>
      <span style={{ flex:1, fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'var(--text-1)' }}>{ep.name}</span>
      {onDel && hov && <button style={S.del} onClick={onDel}><Trash2 size={9}/></button>}
    </div>
  );
}

const S = {
  sidebar: { width:'var(--sidebar-w)', background:'var(--bg-surface)', borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', height:'100vh', flexShrink:0 },
  head: { padding:'13px 11px 10px', borderBottom:'1px solid var(--border)', flexShrink:0, position:'relative' },
  logoRow: { display:'flex', alignItems:'center', gap:8, marginBottom:10 },
  logoMeta: { flex:1, display:'flex', alignItems:'center', gap:7 },
  bell: { position:'relative', background:'none', border:'none', color:'var(--text-2)', cursor:'pointer', padding:4, display:'flex', borderRadius:6 },
  bellBadge: { position:'absolute', top:0, right:0, background:'var(--red)', color:'#fff', fontSize:8, fontWeight:700, borderRadius:99, padding:'0 3px', minWidth:13, textAlign:'center', lineHeight:'13px' },
  wsSel: { display:'flex', alignItems:'center', gap:7, padding:'6px 8px', borderRadius:7, cursor:'pointer', background:'var(--bg-hover)', border:'1px solid var(--border)' },
  wsIcon: { width:22, height:22, borderRadius:5, background:'linear-gradient(135deg,#4f9cf9,#34d399)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:10, fontWeight:800, flexShrink:0 },
  wsLabel: { flex:1, fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  picker: { position:'absolute', top:82, left:8, right:8, background:'var(--bg-elevated)', border:'1px solid var(--border-light)', borderRadius:10, zIndex:200, boxShadow:'var(--shadow-lg)', overflow:'hidden' },
  pickerItem: { display:'flex', alignItems:'center', gap:7, padding:'8px 12px', cursor:'pointer', fontSize:12, color:'var(--text-2)', transition:'background .12s' },
  pickerActive: { background:'var(--accent-dim)', color:'var(--accent)' },
  backBtn: { display:'flex', alignItems:'center', gap:5, background:'none', border:'none', color:'var(--text-3)', fontSize:11, cursor:'pointer', padding:'7px 12px 3px', fontFamily:'var(--font)' },
  projList: { flex:1, overflow:'auto', padding:'6px 8px' },
  secHead: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'4px 6px 6px' },
  secLabel: { fontSize:10, fontWeight:700, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'.7px' },
  projItem: { display:'flex', alignItems:'center', gap:7, padding:'7px 9px', borderRadius:7, cursor:'pointer', marginBottom:1, transition:'background .12s' },
  projDot: { width:5, height:5, borderRadius:'50%', background:'var(--accent)', flexShrink:0 },
  projName: { flex:1, fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  asgBadge: { fontSize:9, color:'var(--text-3)', background:'var(--bg-hover)', padding:'1px 5px', borderRadius:3 },
  noItems: { padding:'14px 8px', color:'var(--text-3)', fontSize:11, textAlign:'center' },
  navWrap: { flex:1, display:'flex', flexDirection:'column', overflow:'hidden' },
  navList: { padding:'6px 6px 2px', flexShrink:0 },
  navItem: { display:'flex', alignItems:'center', gap:8, width:'100%', padding:'7px 10px', borderRadius:7, background:'none', color:'var(--text-2)', fontSize:13, cursor:'pointer', fontFamily:'var(--font)', transition:'all .12s', textAlign:'left', marginBottom:1, fontWeight:500, border:'none' },
  navActive: { background:'var(--accent-dim)', color:'var(--accent)', fontWeight:600 },
  navBadge: { marginLeft:'auto', background:'var(--red)', color:'#fff', fontSize:9, fontWeight:700, borderRadius:99, padding:'1px 5px' },
  tree: { flex:1, display:'flex', flexDirection:'column', overflow:'hidden', borderTop:'1px solid var(--border)' },
  treeTop: { display:'flex', alignItems:'center', gap:4, padding:'7px 7px 4px', flexShrink:0 },
  srchWrap: { flex:1, display:'flex', alignItems:'center', gap:5, background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:6, padding:'4px 8px' },
  srchInput: { background:'transparent', border:'none', fontSize:11, padding:0, width:'100%', boxShadow:'none', height:'auto' },
  treeBody: { flex:1, overflow:'auto', padding:'3px 6px 8px' },
  folderRow: { display:'flex', alignItems:'center', gap:4, padding:'5px 7px', borderRadius:6, cursor:'pointer', color:'var(--text-2)', fontSize:11, marginBottom:1 },
  folderName: { flex:1, fontWeight:500 },
  cnt: { fontSize:9, color:'var(--text-3)', background:'var(--bg-hover)', borderRadius:10, padding:'0 4px' },
  del: { background:'none', border:'none', color:'var(--red)', cursor:'pointer', padding:2, display:'flex', flexShrink:0, opacity:0.7 },
  epRow: { display:'flex', alignItems:'center', gap:6, padding:'5px 10px', borderRadius:6, cursor:'pointer', marginBottom:1, transition:'background .1s' },
  epSel: { background:'var(--accent-dim)' },
  epHov: { background:'var(--bg-hover)' },
  footer: { borderTop:'1px solid var(--border)', padding:'10px' },
  adminBtn: { display:'flex', alignItems:'center', gap:6, width:'100%', padding:'6px 10px', borderRadius:7, background:'none', border:'none', color:'var(--text-2)', fontSize:12, cursor:'pointer', fontFamily:'var(--font)', marginBottom:8, fontWeight:500, transition:'background .12s' },
  userRow: { display:'flex', alignItems:'center', gap:8 },
};
